import * as React from 'react';
import { X } from 'lucide-react';
import { authService } from '@/api';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';
import { useNotification } from '@/hooks';
import { UserI } from '@/interfaces';

interface Props {
	user?: UserI;
}

export const VerificationReminderModal: React.FC<Props> = ({ user }) => {
	const { Modal, show, hide } = useModal();
	const { toastSuccess, toastError } = useNotification();
	const [isResending, setIsResending] = React.useState(false);

	const dismissedKey = user
		? `verification_reminder_dismissed_${user.id}`
		: undefined;

	React.useEffect(() => {
		if (!user || user.is_verified) return;
		if (dismissedKey && sessionStorage.getItem(dismissedKey)) return;

		show();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, user?.is_verified]);

	const handleDismiss = (): void => {
		if (dismissedKey) {
			sessionStorage.setItem(dismissedKey, 'true');
		}
		hide();
	};

	const handleResend = async (): Promise<void> => {
		if (!user?.email || isResending) return;
		setIsResending(true);
		try {
			await authService.resendVerificationEmail(user.email);
			toastSuccess('Verification email sent. Please check your inbox.');
		} catch (error) {
			toastError('Something went wrong. Please try again.');
		} finally {
			setIsResending(false);
		}
	};

	if (!user || user.is_verified) return null;

	return (
		<Modal
			onClose={handleDismiss}
			backgroundColor="white"
			classNameCardContainer="w-full md:!w-[532px] !rounded-[32px]"
			classNameTitle="flex items-center justify-center gap-2 !mb-8 pr-8"
			title={
				<>
					<Icon
						src={Icons.circleGreenExclamation}
						width={75}
						height={32}
						className="shrink-0"
					/>
					<span>Verify Your Email to Continue</span>
				</>
			}
		>
			<button
				type="button"
				onClick={handleDismiss}
				className="absolute right-8 lg:right-10 top-8 outline-none"
			>
				<X className="w-6 h-6 text-black" />
			</button>

			<div className="flex flex-col items-center text-center gap-4 pb-2">
				<Typography type="regular-16" className="!text-gray-0">
					To start uploading documents and track your case progress, you need to
					verify your email address.
				</Typography>
				<Typography type="semibold-16" className="!text-[#6E6A73]">
					Please check your inbox for the verification link.
				</Typography>
				<Button
					label="Resend Verification Email"
					decoration="primary"
					size="fit"
					loading={isResending}
					disabled={isResending}
					onClick={handleResend}
					className="mt-2"
				/>
			</div>
		</Modal>
	);
};
